import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Card,
  CardContent,
  Chip,
  Dialog,
  Grid,
  IconButton,
  Paper,
  Typography,
  makeStyles
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import openSocket from "../../services/socket-io";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import Ticket from "../Ticket";
import { formatWhatsappTime } from "../../helpers/formatWhatsappTime";

const useStyles = makeStyles(theme => ({
  board: {
    height: "100%",
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.default
  },
  column: {
    background: theme.palette.background.paper,
    borderRadius: 8,
    padding: theme.spacing(2),
    minHeight: "100%",
    display: "flex",
    flexDirection: "column",
    border: "1px solid rgba(0, 0, 0, 0.08)"
  },
  columnHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1)
  },
  columnBody: {
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    paddingTop: theme.spacing(1),
    minHeight: 320,
    maxHeight: "calc(100vh - 300px)",
    ...theme.scrollbarStyles
  },
  ticketCard: {
    cursor: "pointer",
    borderLeft: "4px solid transparent",
    flex: "0 0 auto"
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  cardAvatar: {
    width: 38,
    height: 38
  },
  cardHeaderText: {
    flex: 1,
    minWidth: 0
  },
  cardTopRow: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: theme.spacing(1)
  },
  cardName: {
    fontWeight: 600,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  cardTime: {
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap"
  },
  ticketMeta: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: theme.spacing(1)
  },
  lastMessage: {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    marginTop: theme.spacing(0.5)
  },
  emptyState: {
    textAlign: "center",
    color: theme.palette.text.secondary,
    padding: theme.spacing(4, 2)
  },
  ticketDialogPaper: {
    height: "85vh",
    maxHeight: "85vh",
    overflow: "hidden",
    position: "relative"
  },
  ticketDialogClose: {
    position: "absolute",
    top: 6,
    right: 6,
    zIndex: 10,
    backgroundColor: "rgba(255, 255, 255, 0.85)"
  }
}));

const columns = [
  {
    id: "pending",
    title: "Aguardando",
    color: "#f57c00"
  },
  {
    id: "open",
    title: "Em atendimento",
    color: "#1976d2"
  },
  {
    id: "closed",
    title: "Resolvido",
    color: "#2e7d32"
  }
];

const TicketsKanban = ({ selectedQueueIds, showAll }) => {
  const classes = useStyles();
  const [tickets, setTickets] = useState([]);
  const [modalTicketId, setModalTicketId] = useState(null);

  const loadBoard = async () => {
    try {
      const { data } = await api.get("/tickets", {
        params: {
          showAll: true,
          queueIds: JSON.stringify(selectedQueueIds || [])
        }
      });

      setTickets(data.tickets);
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    loadBoard();
  }, [showAll, selectedQueueIds]);

  useEffect(() => {
    const socket = openSocket();
    socket.on("ticket", loadBoard);
    socket.on("appMessage", loadBoard);
    socket.on("contact", loadBoard);

    return () => {
      socket.disconnect();
    };
  }, [selectedQueueIds, showAll]);

  const ticketsByColumn = useMemo(() => {
    return columns.reduce((accumulator, column) => {
      accumulator[column.id] = tickets.filter(ticket => ticket.status === column.id);
      return accumulator;
    }, {});
  }, [tickets]);

  const handleDrop = async (event, status) => {
    event.preventDefault();
    const ticketId = event.dataTransfer.getData("ticketId");
    const ticketUserId = event.dataTransfer.getData("ticketUserId");
    const ticketQueueId = event.dataTransfer.getData("ticketQueueId");

    if (!ticketId) {
      return;
    }

    // move OTIMISTA: o card troca de coluna na hora, sem recarregar o board.
    const previous = tickets;
    setTickets(prev =>
      prev.map(t => (String(t.id) === String(ticketId) ? { ...t, status } : t))
    );

    try {
      await api.put(`/tickets/${ticketId}`, {
        status,
        userId: ticketUserId ? Number(ticketUserId) : null,
        queueId: ticketQueueId ? Number(ticketQueueId) : null
      });
    } catch (err) {
      setTickets(previous);
      toastError(err);
    }
  };

  return (
    <>
      <Grid container spacing={2} className={classes.board}>
        {columns.map(column => {
          const columnTickets = ticketsByColumn[column.id] || [];

          return (
            <Grid item xs={12} md={4} key={column.id}>
              <Paper
                className={classes.column}
                onDragOver={event => event.preventDefault()}
                onDrop={event => handleDrop(event, column.id)}
              >
                <div className={classes.columnHeader}>
                  <Typography variant="h6">{column.title}</Typography>
                  <Chip
                    size="small"
                    label={columnTickets.length}
                    style={{
                      backgroundColor: column.color,
                      color: "#fff"
                    }}
                  />
                </div>
                <div className={classes.columnBody}>
                  {columnTickets.length === 0 ? (
                    <div className={classes.emptyState}>Nenhuma conversa aqui.</div>
                  ) : (
                    columnTickets.map(ticket => (
                      <Card
                        key={ticket.id}
                        className={classes.ticketCard}
                        style={{ borderLeftColor: column.color }}
                        draggable
                        onDragStart={event => {
                          event.dataTransfer.setData("ticketId", String(ticket.id));
                          event.dataTransfer.setData(
                            "ticketUserId",
                            ticket.userId ? String(ticket.userId) : ""
                          );
                          event.dataTransfer.setData(
                            "ticketQueueId",
                            ticket.queueId ? String(ticket.queueId) : ""
                          );
                        }}
                        onClick={() => setModalTicketId(ticket.id)}
                      >
                        <CardContent>
                          <div className={classes.cardHeader}>
                            <Avatar
                              className={classes.cardAvatar}
                              src={ticket.contact?.profilePicUrl}
                            >
                              {(ticket.contact?.name || "?").charAt(0)}
                            </Avatar>
                            <div className={classes.cardHeaderText}>
                              <div className={classes.cardTopRow}>
                                <Typography
                                  variant="subtitle2"
                                  className={classes.cardName}
                                >
                                  {ticket.contact?.name || `Ticket #${ticket.id}`}
                                </Typography>
                                <span className={classes.cardTime}>
                                  {formatWhatsappTime(
                                    ticket.lastMessageAt || ticket.updatedAt
                                  )}
                                </span>
                              </div>
                              {ticket.contact?.partner ? (
                              <span
                                style={{
                                  display: "inline-block",
                                  maxWidth: "100%",
                                  background: "#e0f2f1",
                                  color: "#00695c",
                                  fontSize: "0.72rem",
                                  fontWeight: 600,
                                  padding: "1px 8px",
                                  borderRadius: 10,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  marginTop: 2
                                }}
                              >
                                🤝 {ticket.contact.partner}
                              </span>
                              ) : null}
                            </div>
                          </div>
                          <span
                            style={{
                              display: "inline-block",
                              fontSize: "0.66rem",
                              fontWeight: 700,
                              padding: "1px 8px",
                              borderRadius: 10,
                              marginTop: 6,
                              letterSpacing: 0.3,
                              background:
                                ticket.status === "closed" ? "#eceff1" : "#e8f5e9",
                              color:
                                ticket.status === "closed" ? "#546e7a" : "#2e7d32"
                            }}
                          >
                            {ticket.status === "closed" ? "Fechado" : "Aberto"}
                          </span>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            className={classes.lastMessage}
                          >
                            {ticket.lastMessage || "Sem ultima mensagem"}
                          </Typography>
                          <div className={classes.ticketMeta}>
                            {(ticket.tags || []).map(tag => (
                              <Chip
                                key={tag.id}
                                size="small"
                                label={tag.name}
                                style={{ backgroundColor: tag.color, color: "#fff" }}
                              />
                            ))}
                            {ticket.queue?.name && (
                              <Chip size="small" label={ticket.queue.name} />
                            )}
                            {ticket.whatsapp?.name && (
                              <Chip
                                size="small"
                                label={ticket.whatsapp.name}
                                variant="outlined"
                              />
                            )}
                            {ticket.unreadMessages > 0 && (
                              <Chip
                                size="small"
                                color="secondary"
                                label={`${ticket.unreadMessages} novas`}
                              />
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <Dialog
        open={Boolean(modalTicketId)}
        onClose={() => setModalTicketId(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ className: classes.ticketDialogPaper }}
      >
        <IconButton
          size="small"
          className={classes.ticketDialogClose}
          onClick={() => setModalTicketId(null)}
        >
          <CloseIcon />
        </IconButton>
        {modalTicketId && (
          <Ticket
            key={modalTicketId}
            ticketId={String(modalTicketId)}
            onBack={() => setModalTicketId(null)}
          />
        )}
      </Dialog>
    </>
  );
};

export default TicketsKanban;
